// Run-snapshot read — carved from `src/features/agent-harness/hooks.ts`
// (ARCFE-1079 · D4, the `agents/harness` query slice). Bodies are byte-identical
// to the pre-move source; the ONLY change is that `transport` is a REQUIRED
// parameter here instead of defaulting to `agentHarnessClient`.
//
// That default could not come with them: `agentHarnessClient` is an app-side
// ASSEMBLY (`streamPath`/`eventsPath`/`streamHeaders` resolve the ml-scribe
// origin and mint auth headers — see `data/api/harness.ts`), so a `/data`
// module may not name it. It crosses as a parameter, the same shape the inbox
// carve used for its `enabled` gate; the old-path delegate re-applies the
// default so every existing call site — including the one-argument
// `useQueryRun(observedRunId)` in `src/features/rpa/cua-status` — is unchanged.
import { useQuery } from '@tanstack/react-query';

import type {
  LLMStreamConnectionStatus,
  PublicRunSnapshot,
} from '../../contracts/harness.types';
import { agentHarnessKeys } from '../../contracts/keys';
import type { RunTransport } from '../../contracts/transport';
import { isTurnInFlight, type TurnPhase } from '../harness/turn-lifecycle';

export const runQueryOptions = (
  agentRunId: string | null | undefined,
  transport: RunTransport
) => ({
  queryKey: agentHarnessKeys.run(agentRunId ?? ''),
  enabled: !!agentRunId,
  queryFn: () => transport.getRun(agentRunId!),
});

/**
 * True once a run has reached a terminal status. The harness status union is
 * 3-valued (`running | closed | failed`) — there is no `completed`; a finished
 * run lands on `closed`.
 */
export const isTerminalRunStatus = (
  status: PublicRunSnapshot['status'] | null | undefined
): boolean => status === 'closed' || status === 'failed';

export const useQueryRun = (
  agentRunId: string | null | undefined,
  transport: RunTransport
) => useQuery(runQueryOptions(agentRunId, transport));

/**
 * Snapshot poll cadence for a run query.
 *
 * - Terminal runs never poll.
 * - In-flight turns always poll (settlement).
 * - `pollWhileRunning` keeps polling across idle gaps (chat / legacy).
 * - Otherwise poll only while the SSE stream is down (`error` /
 *   `reconnecting`) so a healthy follow-mode connection does not generate
 *   idle snapshot traffic for hours.
 */
export const computeRunRefetchInterval = (
  run: PublicRunSnapshot | undefined,
  phase: TurnPhase,
  pollWhileRunning: boolean,
  streamStatus?: LLMStreamConnectionStatus
): number | false => {
  if (!run || isTerminalRunStatus(run.status)) return false;
  if (phase !== 'idle' || isTurnInFlight('idle', run.input_state)) return 3000;
  if (pollWhileRunning) return 3000;
  if (streamStatus === 'error' || streamStatus === 'reconnecting') return 3000;
  return false;
};
